import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Eye, Users } from 'lucide-react';
import Button from 'src/componentes/button';
import PanelCollapse from 'src/componentes/panel-collapse';
import useAuth from 'src/hooks/useAuth';
import focusManager from 'src/utils/focus-manager';
import { getZIndexAboveDialog } from 'src/utils/zIndexOverlay';
import { MODULOS } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import { CRITERIOS_PUBLICO_INTERESSADO_PADRAO } from './publicoInteressadoDocumento';
import PublicoInteressadoCriteriosDrawer from './PublicoInteressadoCriteriosDrawer';
import PublicoInteressadoPreviewModal from './PublicoInteressadoPreviewModal';
import PublicoInteressadoContador from './PublicoInteressadoContador';
import PublicoInteressadoCriteriosChips from './PublicoInteressadoCriteriosChips';

export default function PublicoInteressadoCriterios({
  documentoId,
  value,
  onChange,
  disabled = false,
}) {
  const intl = useIntl();
  const { modules, terms } = useAuth();
  const [drawer, setDrawer] = useState(null);
  const [preview, setPreview] = useState(null);
  const criterios = { ...CRITERIOS_PUBLICO_INTERESSADO_PADRAO, ...value };
  const hasLift = modules?.some(
    (module) => module.id === MODULOS.AVALIACAO_DESEMPENHO && module.exibir
  );
  const hasCriterios = (items) =>
    (hasLift && items.tempoMinimoCargoDias != null) ||
    items.tempoMinimoEmpresaDias != null ||
    (hasLift && items.cargos?.length > 0) ||
    items.areas?.length > 0 ||
    items.colaboradores?.length > 0;
  const title = capitalizeFirstLetter(
    intl.formatMessage(
      { id: 'publicoInteressado' },
      { publicointeressado: terms.publicointeressado }
    )
  );

  useEffect(() => {
    setDrawer(null);
    setPreview(null);
  }, [documentoId]);

  const closeDrawer = useCallback(() => {
    setDrawer((current) => (current ? { ...current, open: false } : null));
  }, []);

  const openDrawer = () => {
    setDrawer({
      open: true,
      criterios,
      zIndex: getZIndexAboveDialog(),
      focusTrapPriority: focusManager.getTopPriority() + 0.5,
    });
  };

  const applyDrawer = () => {
    if (disabled) return;
    onChange(drawer.criterios);
    closeDrawer();
  };

  const openPreview = (items) => {
    if (!hasCriterios(items)) return;
    setPreview({
      criterios: {
        ...items,
        tempoMinimoCargoDias: hasLift ? items.tempoMinimoCargoDias : null,
        cargos: hasLift ? items.cargos : [],
      },
      zIndex: getZIndexAboveDialog(),
      focusTrapPriority: focusManager.getTopPriority() + 1,
    });
  };

  return (
    <>
      <PanelCollapse open={false} header={title}>
        <p className="tw:mb-4 tw:text-sm tw:text-muted-foreground">
          {intl.formatMessage({ id: 'msgPublicoInteressadoPorDocumento' })}
        </p>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <Button type="button" leftIcon={Users} onClick={openDrawer}>
            {intl.formatMessage({ id: 'labelDefinirPublico' })}
          </Button>
          <Button
            type="button"
            color="secondary"
            leftIcon={Eye}
            disabled={!hasCriterios(criterios)}
            onClick={() => openPreview(criterios)}
          >
            {intl.formatMessage({ id: 'labelPrevisualizarPublicoInteressado' })}
          </Button>
          <PublicoInteressadoContador
            documentoId={documentoId}
            criterios={criterios}
            hasLift={hasLift}
            hasCriterios={hasCriterios(criterios)}
          />
        </div>
        <PublicoInteressadoCriteriosChips
          criterios={criterios}
          hasLift={hasLift}
          disabled={disabled}
          onChange={onChange}
        />
      </PanelCollapse>
      {drawer && (
        <PublicoInteressadoCriteriosDrawer
          {...drawer}
          documentoId={documentoId}
          title={title}
          disabled={disabled}
          hasLift={hasLift}
          hasCriterios={hasCriterios(drawer.criterios)}
          onChange={(items) =>
            setDrawer((current) => ({ ...current, criterios: items }))
          }
          onClose={closeDrawer}
          onApply={applyDrawer}
          onPreview={() => openPreview(drawer.criterios)}
        />
      )}
      {preview && (
        <PublicoInteressadoPreviewModal
          {...preview}
          documentoId={documentoId}
          hasLift={hasLift}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}
